import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { isReviewItemResolved } from "@utils/reportVisibility";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Tooltip } from "@fronterahealth/frontera-ui-components";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import { Spinner } from "@fronterahealth/frontera-ui-components/ui/spinner";

import { CheckpointMark } from "@components/QaReview/components/CheckpointMark";
import { ReviewViewMenu } from "@components/QaReview/components/ReviewViewMenu";
import { SuggestionNavigator } from "@components/QaReview/components/SuggestionNavigator";
import {
  CARD_INSET_PX,
  REVIEW_BAR_OFFSET_VAR,
  REVIEW_BAR_TRANSITION_MS,
  VIEWPORT_MARGIN_PX,
  barBottomPx,
} from "@components/QaReview/components/reviewBarLayout";
import { useGoToItem } from "@components/QaReview/hooks/useGoToItem";
import { useNavigationItems } from "@components/QaReview/hooks/useNavigationItems";
import { useQaScanState } from "@components/QaReview/hooks/useQaScanState";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import {
  DEFAULT_REVIEW_VIEW,
  isDefaultReviewView,
  useReviewSession,
} from "@components/QaReview/hooks/useReviewSession";
import { useIsReviewPage } from "@components/ReportField/capabilities/flags";

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Checkpoint's mark, as the switch shows it, then Quality Review's state: the words go to icons, then the mark
// goes, as the bar squeezes.
const CheckpointBarStatus: React.FC<{ isIconOnly: boolean }> = ({ isIconOnly: isCompact }) => {
  const { rerun, canRerun } = useReviewData();
  const { isChecking, isFailed } = useQaScanState();

  const tryAgain = isCompact ? (
    <Tooltip text="Try again" showInfoIcon={false} placement="top">
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Try again"
        disabled={!canRerun}
        onClick={rerun}
        // The pre-move name, so RUM history stays continuous.
        data-dd-action-name="Rerun Audit"
        className="text-interaction-primary"
      >
        <ArrowPathIcon className="size-4" />
      </Button>
    </Tooltip>
  ) : (
    <Button
      variant="link"
      size="xs"
      disabled={!canRerun}
      onClick={rerun}
      data-dd-action-name="Rerun Audit"
      className="px-0 text-sm! text-interaction-primary"
    >
      Try again
    </Button>
  );

  return (
    <div className="flex items-center gap-1.5 text-sm">
      <CheckpointMark className="size-4 shrink-0 text-text-secondary" />
      {isChecking && (
        <output className="flex items-center gap-1.5 text-text-tertiary">
          <Spinner aria-hidden role={undefined} className="size-3.5" />
          <span className={cn(isCompact && "sr-only")}>Checking</span>
        </output>
      )}
      {isFailed && (
        <>
          <span className={cn("whitespace-nowrap text-text-error-primary", isCompact && "sr-only")}>
            Didn&apos;t load{!isCompact && " ·"}
          </span>
          {tryAgain}
        </>
      )}
    </div>
  );
};

const renderCheckpointStatus = (isIconOnly: boolean) => <CheckpointBarStatus isIconOnly={isIconOnly} />;

/**
 * Keeps the bar mounted through its exit, so it can drop away after the column has gone. It rises in with a
 * CSS animation on mount; on close it plays the drop and unmounts when that ends (or at once under reduced
 * motion, which runs no animation). A timer stands in if the animation's end never arrives.
 */
const useBarPresence = (open: boolean) => {
  const [isMounted, setIsMounted] = useState(open);
  if (open && !isMounted) setIsMounted(true);
  if (!open && isMounted && prefersReducedMotion()) setIsMounted(false);

  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setIsMounted(false), REVIEW_BAR_TRANSITION_MS + 50);
    return () => clearTimeout(timer);
  }, [open]);

  return { isMounted, onExitEnd: () => setIsMounted(false) };
};

type CardsBox = {
  /** The cards' visible left and right edges, inside the slots' inset, after any transform on the column. */
  left: number;
  right: number;
  viewportWidth: number;
  /** The bar's distance from the viewport's bottom: clear of the footer row (Back / Complete) when there is one. */
  bottom: number;
  /** How far below the bar's bottom the footer row's top is, where the band stops. */
  footerGap: number;
};

// Where the cards and the footer row are, measured rather than assumed, so the bar follows either if it
// changes. The last place is kept while the bar drops away.
const useCardsBox = (open: boolean, anchorRef: RefObject<HTMLElement | null>) => {
  const [box, setBox] = useState<CardsBox | null>(null);
  useEffect(() => {
    const column = anchorRef.current;
    if (!open || !column) return;
    const footer = document.querySelector<HTMLElement>('[data-slot="layout-footer"]');
    const measure = () => {
      const rect = column.getBoundingClientRect();
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const footerHeight = footer?.offsetHeight ?? 0;
      const bottom = barBottomPx(footerHeight, rem);
      setBox({
        left: rect.left + CARD_INSET_PX,
        right: rect.right - CARD_INSET_PX,
        viewportWidth: document.documentElement.clientWidth,
        bottom,
        footerGap: bottom - footerHeight,
      });
    };
    const frame = requestAnimationFrame(measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(column);
    if (footer) observer?.observe(footer);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorRef]);
  return box;
};

// The bar's own height, for the toasts to clear.
const useBarHeight = (barRef: RefObject<HTMLElement | null>, isMounted: boolean) => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const bar = barRef.current;
    if (!isMounted || !bar) return;
    const measure = () => setHeight(Math.ceil(bar.getBoundingClientRect().height));
    // Reports once on observe, then on every change.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(bar);
    return () => observer?.disconnect();
  }, [barRef, isMounted]);
  return height;
};

interface ReviewBarProps {
  /** Checkpoint is the picked side panel; the bar rises in with it and drops away with Outline. */
  open: boolean;
  /** The cards column the bar centers on. */
  anchorRef: RefObject<HTMLElement | null>;
}

/**
 * The review bar on Review and Sign, rendered by the page so it can outlive the column for its exit. It walks
 * the order ReviewPanel publishes (the listed cards on this page, in report order), so it needs no card stack
 * of its own and stays up while the panel lists nothing.
 */
export const ReviewBar: React.FC<ReviewBarProps> = ({ open, anchorRef }) => {
  const isReviewPage = useIsReviewPage();
  const { resolvableQaItems, rootComments } = useReviewData();
  const { state: session, dispatch } = useReviewSession();
  const goToItem = useGoToItem();
  const { isChecking, isFailed } = useQaScanState();
  const { isMounted, onExitEnd } = useBarPresence(open);
  const box = useCardsBox(open, anchorRef);
  // Right-aligned to the cards; a bar wider than them grows left, as far as the viewport's left margin.
  const maxWidth = box ? Math.max(0, box.right - VIEWPORT_MARGIN_PX) : undefined;
  const [portalRoot] = useState(() => document.getElementById("root"));

  const barRef = useRef<HTMLDivElement>(null);
  const barHeight = useBarHeight(barRef, isMounted);
  const bottom = box?.bottom ?? barBottomPx(0, 16);

  // Toasts rise from just above the bar's top. Set while the bar is up, exit included, and cleared only once it
  // has gone.
  useEffect(() => {
    if (!isMounted) return;
    document.body.style.setProperty(REVIEW_BAR_OFFSET_VAR, `${bottom + barHeight}px`);
    return () => {
      document.body.style.removeProperty(REVIEW_BAR_OFFSET_VAR);
    };
  }, [isMounted, bottom, barHeight]);

  const { items, isPending } = useNavigationItems();
  const hasOpenItems = useMemo(
    () => [...resolvableQaItems, ...rootComments].some((item) => !isReviewItemResolved(item)),
    [resolvableQaItems, rootComments],
  );

  if (!isReviewPage || !isMounted || !portalRoot) return null;

  return createPortal(
    <div
      ref={barRef}
      data-testid="review-bar"
      data-state={open ? "open" : "closed"}
      inert={!open || undefined}
      onAnimationEnd={(event) => {
        if (!open && event.target === event.currentTarget) onExitEnd();
      }}
      className={cn(
        "fixed z-50 motion-reduce:animate-none",
        // Unplaced until the cards and the footer row are measured, so it never paints over Back / Complete.
        !box && "invisible right-4",
        open ? "animate-review-bar-in" : "pointer-events-none animate-review-bar-out",
      )}
      style={{
        bottom,
        ...(box && { right: box.viewportWidth - box.right, maxWidth }),
      }}
    >
      {/* A soft band in the page background, so cards slide under the bar cleanly: the cards' width, clear about
          24px above the bar, solid from the bar down to the top of the footer row (never over it). Under the bar,
          above the cards, never clickable; it moves with the bar. */}
      {box && (
        <div
          aria-hidden
          data-testid="review-bar-band"
          className="pointer-events-none absolute right-0 -top-6 -z-10 bg-[linear-gradient(to_bottom,transparent,var(--background)_24px)]"
          style={{
            width: box.right - box.left,
            bottom: -box.footerGap,
          }}
        />
      )}
      <SuggestionNavigator
        items={items}
        activeId={session.active?.itemId ?? null}
        onActivate={goToItem}
        picker={<ReviewViewMenu />}
        status={renderCheckpointStatus}
        maxWidth={maxWidth}
        // Quality Review's flags aren't counted while it checks or after it fails, so nothing open can't be told.
        hasOpenItems={isChecking || isFailed || hasOpenItems}
        isDefaultView={isDefaultReviewView(session.view)}
        isPending={isPending}
        onShowAllOpen={() => dispatch({ type: "setReviewView", view: DEFAULT_REVIEW_VIEW })}
      />
    </div>,
    portalRoot,
  );
};
